import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react'

import { Embed } from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { noop } from '@tribeplatform/react-ui-kit/hooks'

import { ChatAttachment } from '../types.js'

const CHAT_DRAFTS_STORAGE_KEY = 'chatDrafts'

interface ChatDraft {
  content?: string
  attachments?: ChatAttachment[]
  embeds?: Embed[]
}

type ChatDrafts = {
  memberId: string
  drafts: Record<string, ChatDraft>
}

export type DraftChatsContextType = {
  draftsLoaded: boolean
  getChatDraft: (chatId: string) => ChatDraft | undefined
  updateChatDraft: (draft: Partial<ChatDraft>, chatId?: string) => void
}

const createInitialDrafts = (memberId?: string): ChatDrafts => ({
  memberId,
  drafts: {},
})

export const DraftChatsContext = createContext<DraftChatsContextType>(
  {} as DraftChatsContextType,
)

interface DraftChatsProviderProps {
  children: React.ReactNode
  storageKey?: string
}

export const DraftChatsProvider = ({
  children,
  storageKey = CHAT_DRAFTS_STORAGE_KEY,
}: DraftChatsProviderProps) => {
  const { data: authMember } = useAuthMember()
  const [draftsLoaded, setDraftsLoaded] = useState(false)

  const removeAllDrafts = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
      chatDrafts.current = createInitialDrafts(authMember?.id)
    } catch (error) {
      noop(error)
    }
  }, [authMember?.id, storageKey])

  const readDraftsFromStorage = useCallback(() => {
    try {
      const item = localStorage.getItem(storageKey)

      if (!item) {
        return createInitialDrafts(authMember?.id)
      }

      const parsedItem = JSON.parse(item) as ChatDrafts

      const shouldRemoveDrafts = authMember?.id !== parsedItem.memberId
      if (shouldRemoveDrafts) {
        removeAllDrafts()
        return createInitialDrafts(authMember?.id)
      }

      return parsedItem
    } catch (error) {
      noop(error)
      return createInitialDrafts(authMember?.id)
    } finally {
      setDraftsLoaded(true)
    }
  }, [authMember?.id, removeAllDrafts, storageKey])

  const saveDraftsInLocalStorage = useCallback(() => {
    try {
      const drafts: ChatDrafts = {
        memberId: authMember?.id,
        drafts: chatDrafts.current.drafts,
      }

      localStorage.setItem(storageKey, JSON.stringify(drafts))
    } catch (error) {
      noop(error)
    }
  }, [authMember?.id, storageKey])

  const chatDrafts = useRef<ChatDrafts>(createInitialDrafts(authMember?.id))

  useEffect(() => {
    chatDrafts.current = readDraftsFromStorage()
  }, [readDraftsFromStorage])

  useEffect(() => {
    if (authMember?.id && chatDrafts.current.memberId !== authMember.id) {
      chatDrafts.current = readDraftsFromStorage()
    }
  }, [authMember?.id, readDraftsFromStorage])

  const updateChatDraft = useCallback(
    (draft: Partial<ChatDraft>, chatId?: string) => {
      if (!chatId) {
        return
      }

      const currentDraft = chatDrafts.current.drafts[chatId] ?? {}
      const updatedDraft: ChatDraft = {
        ...currentDraft,
        ...draft,
      }

      const updatedDrafts: ChatDrafts = {
        ...chatDrafts.current,
        drafts: {
          ...chatDrafts.current.drafts,
          [chatId]: updatedDraft,
        },
      }

      chatDrafts.current = updatedDrafts
      saveDraftsInLocalStorage()
    },
    [saveDraftsInLocalStorage],
  )

  const getChatDraft = useCallback(
    (chatId: string) => {
      if (!draftsLoaded) {
        return undefined
      }

      return chatDrafts.current.drafts[chatId]
    },
    [draftsLoaded],
  )

  const value = useMemo(
    () => ({
      getChatDraft,
      updateChatDraft,
      draftsLoaded,
    }),
    [getChatDraft, updateChatDraft, draftsLoaded],
  )

  return (
    <DraftChatsContext.Provider value={value}>
      {children}
    </DraftChatsContext.Provider>
  )
}

export const useDraftChats = () => {
  const context = useContext(DraftChatsContext)

  if (!context) {
    throw new Error('useDraftChats must be used within a DraftChatsProvider')
  }

  return context
}
